// GTM dataLayer event tracking utility
import { isAnalyticsPathExcluded, isDevEnv } from './analyticsEnv';
import { pushToDataLayer } from './gtm';
import { markGamePlayed } from './gameIntroStorage';
import type { RecommendedPlayGameType } from './pickRecommendedPlayGame';

function shouldSkipAnalytics(pagePath?: string) {
  if (isDevEnv()) return true;
  return isAnalyticsPathExcluded(pagePath);
}

interface PageViewEvent {
  user_id: string;
  page_path: string;
}

interface RubyPurchasedEvent {
  user_id: string;
  purchase_amount: number;
  currency: string;
}

interface CompleteRegistrationEvent {
  user_id: string;
}

interface GameInitiationEvent {
    user_id: string;
    game_type: 'wheel-spin' | 'quickfire' | 'trader' | 'squares' | 'bullion' | 'sixes';
}

interface PlayTraderSquaresEvent {
  user_id: string;
  outcome: 'win' | 'loss';
}

// Track page view
export const trackPageView = (data: PageViewEvent) => {
    if (shouldSkipAnalytics(data.page_path)) return;
    void pushToDataLayer({
        dataLayer: {
            event: 'new_page_view',
            user_id: data.user_id,
            page_path: data.page_path
        }
    });
};

// Track ruby purchase
export const trackRubyPurchased = (data: RubyPurchasedEvent) => {
    if (shouldSkipAnalytics()) return;
    void pushToDataLayer({
        dataLayer: {
            event: 'ruby_purchased',
            user_id: data.user_id,
            purchase_amount: data.purchase_amount,
            currency: data.currency
        }
    });
};

// Track registration completion
export const trackCompleteRegistration = (data: CompleteRegistrationEvent) => {
    if (shouldSkipAnalytics()) return;
    void pushToDataLayer({
        dataLayer: {
            event: 'CompleteRegistration',
            user_id: data.user_id
        }
    });
};

// Track game initiation
export const trackGameInitiation = (data: GameInitiationEvent) => {
    const lobbyGameTypes: RecommendedPlayGameType[] = ['sixes', 'bullion', 'squares', 'trader'];
    if (lobbyGameTypes.includes(data.game_type as RecommendedPlayGameType)) {
        markGamePlayed(data.game_type as RecommendedPlayGameType);
    }
    if (shouldSkipAnalytics()) return;
    void pushToDataLayer({
        dataLayer: {
            event: 'game_initiation',
            user_id: data.user_id,
            game_type: data.game_type,
            // Clear sticky funnel vars so legacy wheel pushes cannot leak into trader/squares.
            entry_point: null,
            sport: null,
            submission_type: null,
            prediction_id: null,
            fixture_id: null,
            competition_name: null,
        }
    });
};

// Track Trader game completion
export const trackPlayTrader = (data: PlayTraderSquaresEvent) => {
    if (shouldSkipAnalytics()) return;
    void pushToDataLayer({
        dataLayer: {
            event: 'play_trader',
            user_id: data.user_id,
            outcome: data.outcome
        }
    });
};

// Track Squares game completion
export const trackPlaySquares = (data: PlayTraderSquaresEvent) => {
    if (shouldSkipAnalytics()) return;
    void pushToDataLayer({
        dataLayer: {
            event: 'play_squares',
            user_id: data.user_id,
            outcome: data.outcome
        }
    });
};

// Track Bullion contest settlement (prize paid: winnings > 0 => win)
export const trackPlayBullion = (data: PlayTraderSquaresEvent) => {
    if (shouldSkipAnalytics()) return;
    void pushToDataLayer({
        dataLayer: {
            event: 'play_bullion',
            user_id: data.user_id,
            outcome: data.outcome
        }
    });
};

// Track when user clicks the spin button inside the wheel iframe (iframe must postMessage("SPIN_CLICKED"))
export const trackSpinWheelClicked = (data: { user_id?: string }) => {
    if (shouldSkipAnalytics()) return;
    void pushToDataLayer({
        dataLayer: {
            event: 'play_wheel_spinner',
            user_id: data.user_id
        }
    });
};
